import { Spacer, Text } from "@hackthenorth/north";
import { Button } from "north.js";
import React, { useMemo } from "react";
import styled from "styled-components";
import { useClipboard } from "use-clipboard-copy";

import Modal from "src/shared/components/Modal";
import { successToast } from "src/shared/components/Toast";

const DELETION_PROCESSING_DAYS = 30;

const templatePreviewStyle: React.CSSProperties = {
  margin: 0,
  padding: 16,
  borderRadius: 8,
  background: "#f3f4f6",
  border: "1px solid #d1d5db",
  fontSize: 14,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  color: "#0E3460",
};

function buildDeletionEmailTemplate(userEmail: string) {
  return {
    to: "hello@hackthenorth.com",
    subject: "Account deletion request",
    body: `Hi Hack the North team,

I'd like to request deletion of my Hack the North account.

My account email: ${userEmail}
Reason (optional):

Thanks!`,
  };
}

function buildMailtoUrl(template: {
  to: string;
  subject: string;
  body: string;
}) {
  return `mailto:${template.to}?subject=${encodeURIComponent(
    template.subject
  )}&body=${encodeURIComponent(template.body)}`;
}

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  userEmail,
}) => {
  const clipboard = useClipboard();
  const emailTemplate = useMemo(
    () => buildDeletionEmailTemplate(userEmail),
    [userEmail]
  );

  const copyTemplate = () => {
    clipboard.copy(emailTemplate.body);
    successToast("Copied!");
  };

  const openMailClient = () => {
    window.location.href = buildMailtoUrl(emailTemplate);
  };

  return (
    <StyledDeleteAccountModal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete account"
      width="640px"
      actions={
        <>
          <Button color="secondary" size="lg" onClick={copyTemplate}>
            Copy template
          </Button>
          <Spacer width="12px" />
          <Button color="primary" size="lg" onClick={openMailClient}>
            Open in mail client
          </Button>
        </>
      }
    >
      <Text>
        To delete your account, email us at{" "}
        <EmailLink href={`mailto:${emailTemplate.to}`}>
          {emailTemplate.to}
        </EmailLink>{" "}
        with the template below and we&apos;ll process your request within{" "}
        {DELETION_PROCESSING_DAYS} days.
      </Text>
      <Spacer height={24} />
      <pre style={templatePreviewStyle}>{emailTemplate.body}</pre>
    </StyledDeleteAccountModal>
  );
};

const EmailLink = styled.a`
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  text-decoration: underline;
  font-weight: inherit;

  &:hover,
  &:focus {
    opacity: 80%;
  }
`;

const StyledDeleteAccountModal = styled(Modal)`
  border-radius: 8px;
  overflow: hidden;

  & > div:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

export default DeleteAccountModal;
